/**
 * Manages interactive elements in the header, such as dropdown menus and the search panel.
 * This script handles showing/hiding panels on click, manages ARIA states for accessibility,
 * controls keyboard navigation (Enter, Space, Escape), and handles focus management
 * for an accessible user experience.
 */
import { getLenisInstance } from '../../core/scroll-manager';

export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    const lenis = getLenisInstance();
    if (!header) return;

    const nav = header.querySelector('.header__navigation');
    const searchToggle = header.querySelector('.header__search-toggle');
    const searchContainer = header.querySelector('.header__search-container');
    const searchForm = header.querySelector('#header-search-form');
    const searchInput = header.querySelector('#header-search-input');
    const menuPanel = header.querySelector('#header-menu-panel');
    const menuToggle = header.querySelector('.header__menu-toggle');
    const menuToggleText = menuToggle ? menuToggle.querySelector('.header__menu-toggle-text') : null;

    const bookingToggles = header.querySelectorAll('.toggle-booking');
    const allPanels = Array.from(header.querySelectorAll('.sub-menu, .header__search-form'));
    // The mobile menu panel is handled separately by its toggle,
    // but it should be included in `allPanels` so it can be closed by global events.
    if (menuPanel) {
        allPanels.push(menuPanel);
    }

    // The `.header__navigation` element itself can act as a panel in some mobile views.
    // It needs to be included in `allPanels` to be correctly hidden by `hideAllPanels`.
    if (nav) {
        allPanels.push(nav);
    }

    let activeTrigger = null; // To store the element that opened a panel for focus return

    // Set initial ARIA states for accessibility
    allPanels.forEach(panel => panel.setAttribute('aria-hidden', 'true'));
    if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');

    /**
     * Hides all panels, resets ARIA states, and returns focus to the trigger.
     */
    const hideAllPanels = () => {
        const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
        if (!isAnyPanelVisible) {
            return; // Nothing to hide
        }

        allPanels.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });

        if (activeTrigger) {
            activeTrigger.setAttribute('aria-expanded', 'false');
            // Return focus to the element that opened the panel
            activeTrigger.focus();
            activeTrigger = null;
        }

        if (menuPanel) {
            menuPanel.style.height = '0px';
        }
        header.classList.remove('is-panel-active');

        // Handle mobile menu state
        if (menuToggle) {
            document.body.classList.remove('menu-open');
            if (menuToggleText) {
                menuToggleText.textContent = 'Menu';
            }
        }

        // Also ensure the booking menu is closed
        document.body.classList.remove('booking-menu-open');

        // Re-enable Lenis scrolling if it exists
        if (lenis) {
            lenis.start();
        }
    };

    /**
     * Shows a specific panel, sets ARIA states, and manages focus.
     * @param {HTMLElement} panel The panel element to show.
     * @param {HTMLElement} trigger The element that triggered the panel to open.
     */
    const showPanel = (panel, trigger) => {
        // Hide any other visible panel first
        const currentlyVisible = allPanels.find(p => p.classList.contains('is-visible') && p !== panel);
        if (currentlyVisible) {
            currentlyVisible.classList.remove('is-visible');
            currentlyVisible.setAttribute('aria-hidden', 'true');
            if (activeTrigger) {
                activeTrigger.setAttribute('aria-expanded', 'false');
            }
        }

        panel.classList.add('is-visible');
        panel.setAttribute('aria-hidden', 'false');

        // Update the trigger
        activeTrigger = trigger;
        if (activeTrigger) {
            activeTrigger.setAttribute('aria-expanded', 'true');
        }

        // Calculate and set the background panel height using scrollHeight
        // For mobile menu, we control height with CSS, not JS.
        if (panel !== menuPanel && menuPanel) {
            const panelHeight = panel.scrollHeight;
            menuPanel.style.height = `${panelHeight}px`;
            header.classList.add('is-panel-active');
        } else {
            if (menuToggleText) {
                menuToggleText.textContent = 'Close';
            }

            // Disable Lenis scrolling when the mobile menu is open
            if (lenis) {
                lenis.stop();
            }
        }

        // --- Focus Management ---
        // Use a timeout to ensure the panel is visible and focusable before moving focus.
        setTimeout(() => {
            // For mobile menu, focus the first link inside the navigation
            if (panel === searchForm && searchInput) {
                searchInput.focus();
            } else {
                const firstFocusable = panel.querySelector('a, button');
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }
        }, 100); // A small delay can help, especially with transitions.
    };

    // --- Sub-menu click & keydown logic ---
    if (nav) {
        const menuItemsWithChildren = Array.from(nav.querySelectorAll('.menu-item-has-children'));

        menuItemsWithChildren.forEach(item => {
            const link = item.querySelector('a[aria-haspopup="true"]');
            if (!link) return;

            const subMenuId = link.getAttribute('aria-controls');
            const subMenu = document.getElementById(subMenuId);
            if (!subMenu) return;

            const toggleSubMenu = (event) => {
                // This is the crucial part to stop the browser's default navigation
                event.preventDefault();

                const isVisible = subMenu.classList.contains('is-visible');

                if (isVisible) {
                    hideAllPanels();
                } else {
                    showPanel(subMenu, link);
                }
            };

            link.addEventListener('click', toggleSubMenu);

            link.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') { // ' ' is for Space key
                    toggleSubMenu(event);
                }
            });
        });
    }

    // --- Search toggle click logic ---
    if (searchToggle && searchForm) {
        searchToggle.addEventListener('click', () => {
            const isVisible = searchForm.classList.contains('is-visible');
            if (isVisible) {
                hideAllPanels();
            } else {
                showPanel(searchForm, searchToggle);
            }
        });
    }

    // --- Mobile menu toggle click logic ---
    if (menuToggle && menuPanel) {
        menuToggle.addEventListener('click', () => {
            const isVisible = menuPanel.classList.contains('is-visible');
            if (isVisible) {
                hideAllPanels();
            } else {
                // For mobile menu, we also add a class to the body
                document.body.classList.add('menu-open');
                showPanel(menuPanel, menuToggle);
            }
        });
    } else if (menuToggle && nav) {
        // Fallback for when #header-menu-panel doesn't exist, but the toggle and nav do.
        menuToggle.addEventListener('click', () => {
            const isVisible = nav.classList.contains('is-visible');
            if (isVisible) {
                hideAllPanels(); // Use the main hide function
            } else {
                // For mobile menu, we also add a class to the body
                document.body.classList.add('menu-open');
                showPanel(nav, menuToggle); // Then use the main show function
            }
        });
    }

    // --- Booking toggle click logic ---
    if (bookingToggles.length > 0) {
        bookingToggles.forEach(button => {
            button.addEventListener('click', () => {
                const isBookingOpen = document.body.classList.contains('booking-menu-open');
    
                if (isBookingOpen) {
                    // If it's open, just close it.
                    document.body.classList.remove('booking-menu-open');
                } else {
                    // If it's closed, first ensure all other panels (like the main menu) are closed.
                    hideAllPanels();
                    // Then, open the booking menu.
                    document.body.classList.add('booking-menu-open');
                }
            });
        });
    }


    // --- Global event listeners to close panels ---
    document.addEventListener('click', (event) => {
        // If no panel is open, there's nothing to do.
        const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
        if (!isAnyPanelVisible) {
            return;
        }

        // Check if the click was on any known trigger element.
        const isClickOnTrigger = event.target.closest('[aria-haspopup="true"], .header__search-toggle, .header__menu-toggle');

        // Hide panels if the click is outside the main interactive areas:
        // the panel itself and any element that can trigger a panel.
        if (!isClickOnTrigger &&
            (!nav || !nav.contains(event.target)) &&
            (!searchContainer || !searchContainer.contains(event.target))
        ) {
            hideAllPanels();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
            if (isAnyPanelVisible) {
                event.preventDefault(); // Prevent other escape actions if a panel is open
                hideAllPanels();
            }
        }
    });

    // Listen for the custom event dispatched by Barba.js to close panels before a page transition.
    document.addEventListener('barba-leave-start', (event) => {
        hideAllPanels();
    });

    // Hide panels on resize or orientation change, as the layout may have changed.
    window.addEventListener('resize', hideAllPanels);
    window.addEventListener('orientationchange', hideAllPanels);
}